import {
  Backdrop,
  ClickAwayListener,
  Icon,
  IconButton,
  Stack,
  Theme,
  Tooltip,
} from '@mui/material';
import { SyncUser } from '@schooly/api';
import { useUserContext } from '@schooly/components/authentication';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { LanguageSelect } from '@schooly/components/language-select';
import { useFlag } from '@schooly/hooks/use-flag';
import { ProfileIcon } from '@schooly/style';
import React, { FC, useCallback } from 'react';

import { UserContexts } from '../../../constants/userContexts';
import { WithProfile } from '../../../context/profile/WithProfile';
import { NotificationsModal } from '../../../pages/Notification/NotificationModal';
import { ProfileModal } from '../../../pages/ProfileModal/ProfileModal';
import { clearReactQueryStorageCache } from '../../../queryClient';
import UserNav from './UserNav';

interface UserMenuProps {
  isCollapsed?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isCollapsed }) => {
  const { logout, user: currentUser } = useUserContext();

  const [isProfileModalOpen, openProfileModal, closeProfileModal] = useFlag();

  const handleLogOut = useCallback(async () => {
    logout();
    clearReactQueryStorageCache();
  }, [logout]);

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <Stack
        position="relative"
        flexDirection={isCollapsed ? 'column-reverse' : 'row'}
        justifyContent={isCollapsed ? 'center' : 'space-between'}
        alignItems="center"
        mt={1.5}
        gap={1}
        mx={isCollapsed ? 1 : 2.5}
      >
        <ProfileButton
          isCollapsed={isCollapsed}
          currentUser={currentUser}
          onLogOut={handleLogOut}
          onOpenProfile={openProfileModal}
        />
        {!isCollapsed && <LanguageSelect variant="icon" />}
        <NotificationsModal />
      </Stack>

      {
        /*
        Don't use `ProfileModal.open` prop. There should not be two ProfileContext instances
        in the DOM simultaneously as it produces collisions.
        */
        isProfileModalOpen && (
          <WithProfile
            userType="profile"
            id={currentUser.user_id}
            context={UserContexts.Individual}
            onClose={closeProfileModal}
          >
            <ProfileModal />
          </WithProfile>
        )
      }
    </>
  );
};

type ProfileButtonProps = {
  isCollapsed?: boolean;
  currentUser: SyncUser;
  onOpenProfile: () => void;
  onLogOut: () => void;
};
const ProfileButton: FC<ProfileButtonProps> = ({
  currentUser,
  onLogOut,
  onOpenProfile,
  isCollapsed,
}) => {
  const [opened, open, close] = useFlag();

  return (
    <>
      <Backdrop open={opened} invisible sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />
      <ClickAwayListener onClickAway={close}>
        <div>
          <Tooltip
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [isCollapsed ? -4 : -12, -4],
                    },
                  },
                ],
              },
            }}
            componentsProps={{
              tooltip: {
                sx: () => ({
                  width: 300,
                  maxWidth: 300,
                  padding: 0,
                  overflow: 'hidden',
                }),
              },
            }}
            title={
              <UserNav
                user={currentUser}
                onClick={close}
                onProfileOpen={onOpenProfile}
                logOut={onLogOut}
              />
            }
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={close}
            open={opened}
            placement="top-start"
          >
            <IconButton onClick={open} data-test-id="user-avatar-button">
              <AvatarAuth
                sx={(theme: Theme) => ({
                  border: 0,
                  width: theme.spacing(4.5),
                  height: theme.spacing(4.5),
                  backgroundColor: theme.palette.background.paper,
                  outline: opened
                    ? `2px solid ${theme.palette.primary.main}`
                    : `2px solid transparent`,
                  transition: 'all .2s',
                  '.svg-icon': {
                    color: theme.palette.primary.main,
                  },
                })}
                user={currentUser}
              >
                <Icon>
                  <ProfileIcon />
                </Icon>
              </AvatarAuth>
            </IconButton>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default UserMenu;
